const SECTION_SELECTOR = ".image-with-carousel";

const addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
  let dotNodes = [];

  const addDotBtnsWithClickHandlers = () => {
    dotsNode.innerHTML = emblaApi
      .scrollSnapList()
      .map(
        () =>
          '<button class="embla__dot" type="button" aria-label="Slide"></button>'
      )
      .join("");

    dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
    dotNodes.forEach((dotNode, index) => {
      dotNode.addEventListener(
        "click",
        () => {
          emblaApi.scrollTo(index);
        },
        false
      );
    });
  };

  const toggleDotBtnsActive = () => {
    const previous = emblaApi.previousScrollSnap();
    const selected = emblaApi.selectedScrollSnap();

    if (dotNodes[previous]) {
      dotNodes[previous].classList.remove("embla__dot--selected");
    }

    if (dotNodes[selected]) {
      dotNodes[selected].classList.add("embla__dot--selected");
    }
  };

  emblaApi
    .on("init", addDotBtnsWithClickHandlers)
    .on("reInit", addDotBtnsWithClickHandlers)
    .on("init", toggleDotBtnsActive)
    .on("reInit", toggleDotBtnsActive)
    .on("select", toggleDotBtnsActive);

  addDotBtnsWithClickHandlers();
  toggleDotBtnsActive();
};

const addPrevNextBtnsClickHandlers = (emblaApi, sectionNode) => {
  const prevBtn = sectionNode.querySelector(".prev-btn");
  const nextBtn = sectionNode.querySelector(".next-btn");

  if (!prevBtn || !nextBtn) return;

  const togglePrevNextBtnsState = () => {
    if (emblaApi.canScrollPrev()) {
      prevBtn.removeAttribute("disabled");
      prevBtn.classList.remove("carousel-controls-disabled");
    } else {
      prevBtn.setAttribute("disabled", "");
      prevBtn.classList.add("carousel-controls-disabled");
    }

    if (emblaApi.canScrollNext()) {
      nextBtn.removeAttribute("disabled");
      nextBtn.classList.remove("carousel-controls-disabled");
    } else {
      nextBtn.setAttribute("disabled", "");
      nextBtn.classList.add("carousel-controls-disabled");
    }
  };

  prevBtn.addEventListener(
    "click",
    () => {
      emblaApi.scrollPrev();
    },
    false
  );
  nextBtn.addEventListener(
    "click",
    () => {
      emblaApi.scrollNext();
    },
    false
  );

  emblaApi
    .on("init", togglePrevNextBtnsState)
    .on("reInit", togglePrevNextBtnsState)
    .on("select", togglePrevNextBtnsState);

  togglePrevNextBtnsState();
};

const waitForStylesheet = () => {
  return new Promise((resolve) => {
    const link = document.querySelector(
      'link[href*="image-with-carousel-stylesheet"]'
    );

    if (!link) {
      resolve();
      return;
    }

    if (link.sheet || link.media === "all") {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      link.removeEventListener("load", onLoad);
      link.removeEventListener("error", onLoad);
      resolve();
    }, 3000);

    const onLoad = () => {
      clearTimeout(timeout);
      resolve();
    };

    link.addEventListener("load", onLoad);
    link.addEventListener("error", onLoad);
  });
};

const waitForImages = (container, timeout = 1500) => {
  const images = Array.from(container.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let resolved = false;
    let remaining = images.length;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };

    const handleDone = () => {
      if (resolved) return;
      remaining -= 1;
      if (remaining <= 0) {
        finish();
      }
    };

    for (const image of images) {
      if (image.complete) {
        handleDone();
        continue;
      }

      const onComplete = () => {
        image.removeEventListener("load", onComplete);
        image.removeEventListener("error", onComplete);
        handleDone();
      };

      image.addEventListener("load", onComplete, { once: true });
      image.addEventListener("error", onComplete, { once: true });
    }

    if (remaining <= 0) {
      finish();
      return;
    }

    setTimeout(finish, timeout);
  });
};

const initializeBackgroundCarousels = async (backgroundApis, sectionNode) => {
  await waitForStylesheet();

  const backgroundViewports = Array.from(
    sectionNode.querySelectorAll(".background-embla__viewport")
  );

  if (!backgroundViewports.length) return;

  await Promise.all(
    backgroundViewports.map(async (viewport) => {
      await waitForImages(viewport);

      if (!sectionNode.contains(viewport)) return;

      const api = EmblaCarousel(viewport, { watchDrag: false });
      backgroundApis.push(api);

      const images = Array.from(viewport.querySelectorAll("img"));
      for (const image of images) {
        if (image.complete) continue;
        image.addEventListener(
          "load",
          () => {
            api.reInit();
          },
          { once: true }
        );
      }
    })
  );
};

const ensureEmblaAvailable = async () => {
  if (typeof EmblaCarousel !== "undefined") return;

  await new Promise((resolve) => {
    const intervalId = setInterval(() => {
      if (typeof EmblaCarousel !== "undefined") {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        resolve();
      }
    }, 50);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      resolve();
    }, 5000);
  });
};

const initializeSection = async (sectionNode) => {
  if (sectionNode.dataset.carouselInitialized === "true") return;

  const emblaNode = sectionNode.querySelector(".embla");
  const viewportNode = emblaNode?.querySelector(".embla__viewport");
  if (!emblaNode || !viewportNode) return;

  await ensureEmblaAvailable();

  if (typeof EmblaCarousel === "undefined") {
    console.error("EmblaCarousel library not loaded");
    return;
  }

  const dotsNode = sectionNode.querySelector(".embla__dots");
  const backgroundApis = [];
  const emblaApi = EmblaCarousel(viewportNode);

  if (dotsNode) {
    addDotBtnsAndClickHandlers(emblaApi, dotsNode);
  }

  addPrevNextBtnsClickHandlers(emblaApi, sectionNode);

  const syncBackgrounds = (jump = false) => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    for (const api of backgroundApis) {
      if (!api) continue;
      api.scrollTo(selectedIndex, jump);
    }
  };

  emblaApi.on("select", syncBackgrounds).on("reInit", syncBackgrounds);

  initializeBackgroundCarousels(backgroundApis, sectionNode).then(() => {
    syncBackgrounds(true);
  });

  sectionNode.dataset.carouselInitialized = "true";
};

const initializeCarousel = () => {
  const sectionNodes = document.querySelectorAll(SECTION_SELECTOR);
  sectionNodes.forEach((section) => {
    initializeSection(section);
  });
};

const onReady = () => {
  initializeCarousel();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", onReady);
} else {
  onReady();
}

document.addEventListener("shopify:section:load", (event) => {
  if (event.target.matches(SECTION_SELECTOR)) {
    initializeSection(event.target);
    return;
  }

  const nestedSection = event.target.querySelector(SECTION_SELECTOR);
  if (nestedSection) {
    initializeSection(nestedSection);
  }
});
